import orderBy from 'lodash/orderBy';

const ANALYTICS_METRICS = {
  authorFollows: 'section_author_follows',
  itemCTR: 'item_ctr',
  itemFlips: 'item_flips',
  itemImpressions: 'item_displays',
  itemLikes: 'item_likes',
  itemShares: 'item_shares',
  itemSocialEngagements: 'item_social_engagements',
  itemViews: 'item_views',
  opensOffFlipboard: 'from_external_section_views',
  opensOnFlipboard: 'from_internal_section_views',
  sectionEngagements: 'section_engagements',
  sectionFlips: 'section_flips',
  sectionFollows: 'section_follows',
  sectionGatewayComments: 'section_comments',
  sectionGatewayFlips: 'section_flips',
  sectionGatewayLikes: 'section_likes',
  sectionGatewayShares: 'section_shares',
  sectionImpressions: 'section_displays',
  sectionLikes: 'section_likes',
  sectionShares: 'section_shares',
  sectionTimeSpent: 'section_time_spent',
  sectionViews: 'section_views',
};

const ANALYTICS_DIMENSIONS = {
  country: 'countryname',
  deviceType: 'appmode',
  OS: 'prod_type',
};

const DEVICE_TYPES = {
  androidPhone: 'aphone',
  androidTablet: 'apad',
  iPhone: 'iphone',
  iPad: 'ipad',
};

const OS_TYPES = {
  android: 'android',
  briefing: 'briefing_plus',
  ios: 'ios',
};

/**
 * Returns the key/values of a given dimension
 * @param {Object} metrics
 * @param metricKey - one of ANALYTICS_METRICS
 * @param dimension - one of ANALYTICS_DIMENSIONS
 * @returns {Array<Object>} - Array of Objects with key, value
 */
function getDimensionValues(metrics, metricKey, dimension) {
  if (!metrics || !metrics[metricKey]) {
    return [];
  }
  const { drill } = metrics[metricKey];
  if (!drill || !drill[dimension]) {
    return [];
  }
  const dimensions = drill[dimension];
  return orderBy(
    Object.keys(dimensions)
      .map((d) => ({
        key: d,
        value: dimensions[d] || 0,
      }))
      .filter((d) => d.key && d.value),
    ['value'],
    ['desc'],
  );
}

/**
 * Returns the value of a given key inside of Flout metrics
 * @param {Object} metrics
 * @param {String} key - one of ANALYTICS_METRICS
 */
function getMetricValue(metrics, key, defaultValue = 0) {
  if (!metrics || !metrics[key]) {
    return defaultValue;
  }

  const { value } = metrics[key];
  if (!value) {
    return defaultValue;
  }

  return value || defaultValue;
}

// SPECIAL CASE: Cap item views to <= item impressions
// TODO: Remove hacks once metrics collection root
// issue is fixed.
function getCappedItemViews(metrics) {
  const impressions = getMetricValue(
    metrics,
    ANALYTICS_METRICS.itemImpressions,
  );
  const views = getMetricValue(metrics, ANALYTICS_METRICS.itemViews);
  return views > impressions ? impressions : views;
}

// SPECIAL CASE: Cap CTR to max 100%
// TODO: Remove hacks once metrics collection root
// issue is fixed.
function getCappedItemCtr(metrics) {
  const ctr = getMetricValue(metrics, ANALYTICS_METRICS.itemCTR);
  return ctr > 1.0 ? 1.0 : ctr;
}

/**
 * Aggregates device dimensions into phone & tablet
 * @param {Object} metrics
 * @returns {Array<Object>} - Object containing key and values for phone/tablet
 */
function getAggregateDeviceDimensions(metrics) {
  if (!metrics || !metrics[ANALYTICS_METRICS.sectionViews]) {
    return [];
  }
  const { drill } = metrics[ANALYTICS_METRICS.sectionViews];
  if (!drill || !drill[ANALYTICS_DIMENSIONS.deviceType]) {
    return [];
  }

  const dimensions = drill[ANALYTICS_DIMENSIONS.deviceType];
  const tablets =
    (dimensions[DEVICE_TYPES.androidTablet] || 0) +
    (dimensions[DEVICE_TYPES.iPad] || 0);
  const phones =
    (dimensions[DEVICE_TYPES.androidPhone] || 0) +
    (dimensions[DEVICE_TYPES.iPhone] || 0);
  const deviceDimensions = [
    { key: 'phone', value: phones },
    { key: 'tablet', value: tablets },
  ];
  return orderBy(deviceDimensions, ['value'], ['desc']);
}

function getAggregateOSDimensions(metrics) {
  if (!metrics || !metrics[ANALYTICS_METRICS.sectionViews]) {
    return [];
  }
  const { drill } = metrics[ANALYTICS_METRICS.sectionViews];
  if (!drill || !drill[ANALYTICS_DIMENSIONS.OS]) {
    return [];
  }

  const dimensions = drill[ANALYTICS_DIMENSIONS.OS];
  const android =
    (dimensions[OS_TYPES.android] || 0) + (dimensions[OS_TYPES.briefing] || 0);
  const ios = dimensions[OS_TYPES.ios] || 0;
  const deviceDimensions = [
    { key: 'android', value: android },
    { key: 'ios', value: ios },
  ];
  return orderBy(deviceDimensions, ['value'], ['desc']);
}

const DATA_COLLECTION_CUTOFF_GMT_HOUR = 4;
/**
 * Determines if the timestamp is within analytics being available
 * @param {Number} createdTimestamp
 * @returns {Boolean} - whether the timestamp is within range of having metrics
 */
function defaultAnalyticsCutoffTime() {
  const now = new Date();
  return Date.UTC(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    DATA_COLLECTION_CUTOFF_GMT_HOUR,
  );
}
function hasAnalytics(
  createdTimestamp,
  cutoffTime = defaultAnalyticsCutoffTime(),
) {
  return createdTimestamp < cutoffTime;
}

function getSectionMetricsValue(metrics, key, localizeValue = true) {
  if (!metrics) {
    return null;
  }

  let value = getMetricValue(metrics, key);

  // Hack to prevent more views than impressions
  // TODO: Remove once fixed properly
  if (key === ANALYTICS_METRICS.itemViews) {
    value = getCappedItemViews(metrics);
  }
  if (key === ANALYTICS_METRICS.itemCTR) {
    value = getCappedItemCtr(metrics);
  }

  return localizeValue ? value.toLocaleString() : value;
}

function getMinsPerOpen(metrics) {
  if (!metrics) {
    return null;
  }
  const timeSpent = getSectionMetricsValue(
    metrics,
    ANALYTICS_METRICS.sectionTimeSpent,
    false,
  );
  const views = getSectionMetricsValue(
    metrics,
    ANALYTICS_METRICS.sectionViews,
    false,
  );
  const minsPerOpen = views && timeSpent ? timeSpent / (views * 60) : 0;
  return minsPerOpen.toFixed(1);
}
/**
 * Returns whether metrics are empty or not
 * @param {Object} metrics - Flout metrics
 * @return {Boolean} - true is has metrics, false if no values are found.
 */
function hasMetrics(metrics) {
  return Object.values(metrics).some((m) => m.value);
}

export {
  ANALYTICS_METRICS,
  ANALYTICS_DIMENSIONS,
  getMetricValue,
  getDimensionValues,
  getAggregateDeviceDimensions,
  getAggregateOSDimensions,
  getSectionMetricsValue,
  getMinsPerOpen,
  hasMetrics,
  hasAnalytics,
  getCappedItemViews,
  getCappedItemCtr,
};
